import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
    Chip, IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, MenuItem, Snackbar, Alert, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { nodeApiPath } from '../../utils/paths';
import { usePermisos } from '../../utils/usePermisos';
import {
    bibliotecaTiposJWT, bibliotecaReferenciasJWT, bibliotecaStatsJWT,
    bibliotecaListarJWT, bibliotecaCrearJWT, bibliotecaEditarJWT,
    bibliotecaEliminarJWT, bibliotecaSiguienteNumeroJWT
} from '../../api/endpoints';

const BIBLIO_COLOR = '#6d4c41';
const BIBLIO_LIGHT = '#efebe9';

// Normaliza texto en MAYÚSCULAS a Title Case
const titleCase = (str) => {
    if (!str) return '';
    if (str === str.toUpperCase() && str.length > 3) {
        return str.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
    }
    return str;
};

const getFileIcon = (url) => {
    if (!url) return <DescriptionIcon fontSize="small" sx={{ color: '#999' }} />;
    const ext = url.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />;
    if (['doc', 'docx'].includes(ext)) return <DescriptionIcon fontSize="small" sx={{ color: '#1565c0' }} />;
    if (['xls', 'xlsx'].includes(ext)) return <DescriptionIcon fontSize="small" sx={{ color: '#2e7d32' }} />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <DescriptionIcon fontSize="small" sx={{ color: '#f57c00' }} />;
    return <DescriptionIcon fontSize="small" sx={{ color: '#666' }} />;
};

export default function BibliotecaHome() {
    const { tienePermiso } = usePermisos();
    const puedeCrear = tienePermiso('BIBLIOTECA DE DOCUMENTOS', 'INSERT');
    const puedeEditar = tienePermiso('BIBLIOTECA DE DOCUMENTOS', 'UPDATE');
    const puedeEliminar = tienePermiso('BIBLIOTECA DE DOCUMENTOS', 'DELETE');
    const puedeVerRestringidos = tienePermiso('BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS', 'SELECT');

    const [loading, setLoading] = useState(false);
    const [documentos, setDocumentos] = useState([]);
    const [total, setTotal] = useState(0);
    const [tipos, setTipos] = useState([]);
    const [referencias, setReferencias] = useState([]);
    const [stats, setStats] = useState({ total: 0, con_adjunto: 0, tipos_usados: 0, anios: [] });

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [refFiltro, setRefFiltro] = useState('');
    const [anioFiltro, setAnioFiltro] = useState('');
    // Backend ya filtra solo documentos con archivo real
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [orderBy, setOrderBy] = useState('Id');
    const [order, setOrder] = useState('desc');

    // Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('crear'); // 'crear' | 'editar'
    const [formData, setFormData] = useState({ tipoId: '', numero: '', anio: new Date().getFullYear(), descripcion: '', fojas: '', iniciador: '', keys: '', referenciaId: '' });
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });

    // Snackbar
    const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });

    // Preview
    const [previewUrl, setPreviewUrl] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await bibliotecaListarJWT({
                busqueda, tipoId: tipoFiltro, referenciaId: refFiltro, anio: anioFiltro,
                page: page + 1, pageSize, verRestringidos: puedeVerRestringidos ? '1' : '0'
            });
            if (r.ok) { setDocumentos(r.documentos || []); setTotal(r.total || 0); }
        } catch { /* handled by interceptor */ }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busqueda, tipoFiltro, refFiltro, anioFiltro, page, pageSize, puedeVerRestringidos]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        Promise.all([bibliotecaTiposJWT(), bibliotecaReferenciasJWT(), bibliotecaStatsJWT()])
            .then(([t, r, s]) => {
                if (t?.ok) setTipos(t.tipos || []);
                if (r?.ok) setReferencias(r.referencias || []);
                if (s?.ok) setStats(s);
            }).catch(() => {});
    }, []);

    const handleSearch = () => { setPage(0); fetchData(); };

    const handleSort = (col) => {
        setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const sorted = [...documentos].sort((a, b) => {
        let aV = a[orderBy], bV = b[orderBy];
        if (typeof aV === 'string') { aV = aV?.toLowerCase() || ''; bV = bV?.toLowerCase() || ''; }
        if (aV < bV) return order === 'asc' ? -1 : 1;
        if (aV > bV) return order === 'asc' ? 1 : -1;
        return 0;
    });

    // Dialog handlers
    const handleNuevo = async () => {
        setDialogMode('crear');
        setFormData({ tipoId: '', numero: '', anio: new Date().getFullYear(), descripcion: '', fojas: '', iniciador: '', keys: '', referenciaId: '' });
        setEditId(null);
        setSelectedFile(null);
        setDialogOpen(true);
    };

    const handleEditar = (doc) => {
        setDialogMode('editar');
        setFormData({
            tipoId: doc.TipoId, numero: doc.Numero, anio: doc.Anio,
            descripcion: doc.Descripcion || '', fojas: doc.Fojas || '',
            iniciador: doc.Iniciador || '', keys: doc.Keywords || '',
            referenciaId: doc.Id_Referencia || ''
        });
        setEditId(doc.Id);
        setSelectedFile(null);
        setDialogOpen(true);
    };

    const handleTipoChange = async (tipoId) => {
        setFormData(f => ({ ...f, tipoId }));
        if (tipoId && dialogMode === 'crear') {
            try {
                const r = await bibliotecaSiguienteNumeroJWT(tipoId, formData.anio);
                if (r?.ok) setFormData(f => ({ ...f, numero: r.numero }));
            } catch { /* ignore */ }
        }
    };

    const handleSave = async () => {
        if (!formData.tipoId || !formData.descripcion) {
            setSnack({ open: true, text: 'Tipo y descripción son obligatorios', severity: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const r = dialogMode === 'crear'
                ? await bibliotecaCrearJWT(formData)
                : await bibliotecaEditarJWT(editId, formData);
            if (r?.ok) {
                // Subir archivo si se seleccionó uno
                const docId = dialogMode === 'crear' ? r.id : editId;
                if (selectedFile && docId) {
                    const formDataUpload = new FormData();
                    formDataUpload.append('file', selectedFile);
                    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                    try {
                        await fetch(nodeApiPath(`biblioteca/${docId}/upload`), {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: formDataUpload,
                        });
                    } catch { /* upload error no bloquea */ }
                }
                setSnack({ open: true, text: dialogMode === 'crear' ? 'Documento creado' : 'Documento actualizado', severity: 'success' });
                setDialogOpen(false);
                setSelectedFile(null);
                fetchData();
                bibliotecaStatsJWT().then(s => { if (s?.ok) setStats(s); });
            } else {
                setSnack({ open: true, text: r?.mensaje || 'Error', severity: 'error' });
            }
        } catch { setSnack({ open: true, text: 'Error de conexión', severity: 'error' }); }
        setSaving(false);
    };

    const handleDelete = async () => {
        if (!deleteDialog.doc) return;
        try {
            const r = await bibliotecaEliminarJWT(deleteDialog.doc.Id);
            if (r?.ok) {
                setSnack({ open: true, text: 'Documento eliminado', severity: 'success' });
                setDeleteDialog({ open: false, doc: null });
                fetchData();
            } else {
                setSnack({ open: true, text: r?.mensaje || 'Error al eliminar', severity: 'error' });
            }
        } catch { setSnack({ open: true, text: 'Error', severity: 'error' }); }
    };

    const handleVerArchivo = (doc) => {
        if (!doc.URL_Adjunto) return;
        const token = localStorage.getItem('SIGIF_JWT_TOKEN');
        const url = nodeApiPath(`biblioteca/archivo/${encodeURIComponent(doc.URL_Adjunto)}`) + `?token=${token}`;
        if (doc.URL_Adjunto.toLowerCase().endsWith('.pdf')) {
            setPreviewUrl(url);
        } else {
            window.open(url, '_blank');
        }
    };

    const columns = [
        { id: 'Codigo', label: 'Código' },
        { id: 'Tipo', label: 'Tipo' },
        { id: 'Descripcion', label: 'Descripción' },
        { id: 'Iniciador', label: 'Iniciador' },
        { id: 'Referencia', label: 'Referencia' },
    ];

    return (
        <Box>
            {/* Header */}
            <Paper sx={{ borderRadius: '4px', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ background: `linear-gradient(135deg, ${BIBLIO_COLOR} 0%, #8d6e63 100%)`, px: 3, py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LibraryBooksIcon /> Biblioteca Institucional
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Repositorio de documentos, manuales y normativas
                            </Typography>
                        </Box>
                        {puedeCrear && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNuevo}
                                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                                Nuevo Documento
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Stats */}
                <Box sx={{ display: 'flex', gap: 2, p: 2, backgroundColor: BIBLIO_LIGHT }}>
                    <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', borderRadius: '4px' }}>
                        <Typography variant="h5" fontWeight={700} color={BIBLIO_COLOR}>{stats.total?.toLocaleString()}</Typography>
                        <Typography variant="caption">Documentos</Typography>
                    </Paper>
                    <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', borderRadius: '4px' }}>
                        <Typography variant="h5" fontWeight={700} color="primary">{stats.con_adjunto?.toLocaleString()}</Typography>
                        <Typography variant="caption">Con Adjunto</Typography>
                    </Paper>
                    <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', borderRadius: '4px' }}>
                        <Typography variant="h5" fontWeight={700} color="#f57c00">{stats.tipos_usados}</Typography>
                        <Typography variant="caption">Tipos en Uso</Typography>
                    </Paper>
                </Box>

                {/* Filtros */}
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField size="small" fullWidth placeholder="Buscar por código, descripción, keywords..."
                                value={busqueda} onChange={e => setBusqueda(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField select size="small" fullWidth label="Tipo" value={tipoFiltro}
                                onChange={e => { setTipoFiltro(e.target.value); setPage(0); }}>
                                <MenuItem value="">Todos</MenuItem>
                                {tipos.map(t => <MenuItem key={t.Id} value={t.Id}>{t.Preposicion} - {titleCase(t.Denominacion)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField select size="small" fullWidth label="Referencia" value={refFiltro}
                                onChange={e => { setRefFiltro(e.target.value); setPage(0); }}>
                                <MenuItem value="">Todas</MenuItem>
                                {referencias.map(r => <MenuItem key={r.Id} value={r.Id}>{titleCase(r.Referencia)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField select size="small" fullWidth label="Año" value={anioFiltro}
                                onChange={e => { setAnioFiltro(e.target.value); setPage(0); }}>
                                <MenuItem value="">Todos</MenuItem>
                                {(stats.anios || []).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <Button variant="contained" fullWidth size="small" startIcon={<SearchIcon />} onClick={handleSearch}
                                sx={{ height: 40, backgroundColor: BIBLIO_COLOR, '&:hover': { backgroundColor: '#5d4037' } }}>
                                Buscar
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ borderRadius: '4px' }}>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: BIBLIO_LIGHT }}>
                                <TableCell sx={{ width: 30 }}></TableCell>
                                {columns.map(col => (
                                    <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                {(puedeEditar || puedeEliminar) && <TableCell align="center">Acciones</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No se encontraron documentos</TableCell></TableRow>
                            ) : sorted.map((doc, idx) => (
                                <TableRow key={doc.Id} hover sx={{
                                    cursor: 'pointer',
                                    backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                                }}
                                    onClick={() => handleVerArchivo(doc)}>
                                    <TableCell>{getFileIcon(doc.URL_Adjunto)}</TableCell>
                                    <TableCell>
                                        <strong>{doc.Codigo}</strong>
                                        {doc.Restringido === 1 && <Tooltip title="Restringido"><LockIcon sx={{ fontSize: 14, ml: 0.5, color: '#f44336' }} /></Tooltip>}
                                    </TableCell>
                                    <TableCell><Chip label={titleCase(doc.Tipo)} size="small" variant="outlined" /></TableCell>
                                    <TableCell sx={{ maxWidth: 350 }}>
                                        <Typography variant="body2" noWrap title={titleCase(doc.Descripcion)}>{titleCase(doc.Descripcion)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{titleCase(doc.Iniciador) || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{titleCase(doc.Referencia) || '-'}</TableCell>
                                    {(puedeEditar || puedeEliminar) && (
                                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                                            {puedeEditar && <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditar(doc)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                                            {puedeEliminar && <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, doc })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {total > 0 && (
                    <TablePagination component="div" count={total} page={page}
                        onPageChange={(e, p) => setPage(p)} rowsPerPage={pageSize}
                        onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
                )}
            </TableContainer>

            {/* Dialog Crear/Editar */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ backgroundColor: BIBLIO_COLOR, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{dialogMode === 'crear' ? 'Nuevo Documento' : 'Editar Documento'}</span>
                    <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select fullWidth size="small" label="Tipo *" value={formData.tipoId}
                                onChange={e => handleTipoChange(e.target.value)} disabled={dialogMode === 'editar'}>
                                {tipos.map(t => <MenuItem key={t.Id} value={t.Id}>{t.Preposicion} - {titleCase(t.Denominacion)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <TextField fullWidth size="small" label="Número" value={formData.numero}
                                onChange={e => setFormData(f => ({ ...f, numero: e.target.value.replace(/\D/g, '') }))} disabled={dialogMode === 'editar'}
                                placeholder="Ej: 001" />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <TextField fullWidth size="small" label="Año" value={formData.anio}
                                onChange={e => setFormData(f => ({ ...f, anio: e.target.value.replace(/\D/g, '') }))} disabled={dialogMode === 'editar'}
                                placeholder="2026" />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth size="small" label="Descripción *" multiline rows={2} value={formData.descripcion}
                                onChange={e => setFormData(f => ({ ...f, descripcion: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Iniciador" value={formData.iniciador}
                                onChange={e => setFormData(f => ({ ...f, iniciador: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Fojas" type="number" value={formData.fojas}
                                onChange={e => setFormData(f => ({ ...f, fojas: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select fullWidth size="small" label="Referencia" value={formData.referenciaId}
                                onChange={e => setFormData(f => ({ ...f, referenciaId: e.target.value }))}>
                                <MenuItem value="">Ninguna</MenuItem>
                                {referencias.map(r => <MenuItem key={r.Id} value={r.Id}>{titleCase(r.Referencia)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField fullWidth size="small" label="Palabras clave" value={formData.keys}
                                onChange={e => setFormData(f => ({ ...f, keys: e.target.value }))}
                                helperText="Separar con espacios" />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ border: '1px dashed #bbb', borderRadius: '4px', p: 1.5, textAlign: 'center', backgroundColor: '#fafafa' }}>
                                <input
                                    type="file"
                                    id="biblioteca-file-upload"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                                    style={{ display: 'none' }}
                                    onChange={e => setSelectedFile(e.target.files[0] || null)}
                                />
                                <label htmlFor="biblioteca-file-upload">
                                    <Button variant="outlined" component="span" size="small" startIcon={<DescriptionIcon />}
                                        sx={{ textTransform: 'none' }}>
                                        {selectedFile ? selectedFile.name : 'Seleccionar archivo adjunto'}
                                    </Button>
                                </label>
                                {selectedFile && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                )}
                                {dialogMode === 'crear' && !selectedFile && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#f57c00' }}>
                                        Se recomienda adjuntar un archivo
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}
                        sx={{ backgroundColor: BIBLIO_COLOR, '&:hover': { backgroundColor: '#5d4037' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : dialogMode === 'crear' ? 'Crear' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, doc: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: '#d32f2f' }}>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>¿Eliminar el documento <strong>{deleteDialog.doc?.Codigo}</strong>?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{deleteDialog.doc?.Descripcion}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, doc: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* PDF Preview */}
            <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '4px', height: '85vh' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <span>Vista Previa</span>
                    <IconButton onClick={() => setPreviewUrl(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {previewUrl && <iframe src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Preview" />}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">{snack.text}</Alert>
            </Snackbar>
        </Box>
    );
}
